import { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { db } from '../firebase';
import { collection, addDoc, getDocs, deleteDoc, updateDoc, doc, query, where, Timestamp } from 'firebase/firestore';
import { updateStreak } from '../utils/streakUtils';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from "sonner";
import '../styles/StudyTimePage.css';

import TimerDisplay from '../components/study/TimerDisplay';
import StudyEntryList from '../components/study/StudyEntryList';
import FocusOverlay from '../components/study/FocusOverlay';
import { FiClock, FiSliders, FiPlus, FiList } from 'react-icons/fi';


function StudyTimePage() {
  const userId = localStorage.getItem('userId');
  const location = useLocation();

  // ── Mode & Focus ──
  const [mode, setMode] = useState('chronometer');
  const [focusMode, setFocusMode] = useState(false);
  const [showManualModal, setShowManualModal] = useState(false);
  const [showEntriesModal, setShowEntriesModal] = useState(false);

  // ── Chronometer ──
  const [chronoTime, setChronoTime] = useState(() => Number(localStorage.getItem('chronoTime') || 0));
  const [chronoRunning, setChronoRunning] = useState(() => JSON.parse(localStorage.getItem('chronoRunning') || 'false'));
  const chronoInterval = useRef(null);

  // ── Timer ──
  const [timerHours, setTimerHours] = useState(0);
  const [timerMinutes, setTimerMinutes] = useState(0);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [timerRemaining, setTimerRemaining] = useState(() => Number(localStorage.getItem('timerRemaining') || 0));
  const [timerRunning, setTimerRunning] = useState(() => JSON.parse(localStorage.getItem('timerRunning') || 'false'));
  const [timerStartDuration, setTimerStartDuration] = useState(() => Number(localStorage.getItem('timerStartDuration') || 0));
  const timerInterval = useRef(null);

  // ── Manual Add ──
  const [manualHours, setManualHours] = useState(0);
  const [manualMinutes, setManualMinutes] = useState(0);

  // ── Entries ──
  const [entries, setEntries] = useState([]);
  const [selectedDate, setSelectedDate] = useState('');
  const [editingEntryId, setEditingEntryId] = useState(null);
  const [editHours, setEditHours] = useState(0);
  const [editMinutes, setEditMinutes] = useState(0);
  const [editSeconds, setEditSeconds] = useState(0);

  // ── Format ──
  const formatTime = useCallback((seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  }, []);

  // ── Fetch entries ──
  useEffect(() => {
    if (!userId) return;
    (async () => {
      try {
        const q = query(collection(db, 'studyEntries'), where('userId', '==', userId));
        const snap = await getDocs(q);
        setEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) { toast.error(`Kayıtlar yüklenemedi: ${err.message}`); }
    })();
  }, [userId]);

  // ── Auto Start ──
  useEffect(() => {
    if (location.state?.autoStart) {
      setMode('chronometer');
      if (!chronoRunning) {
        setChronoRunning(true);
        localStorage.setItem('chronoRunning', 'true');
        toast.success('Odaklanma süren başladı! Başarılar 🚀');
      }
      // Remove state so it doesn't auto-start on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, chronoRunning]);

  // ── Chronometer interval ──
  useEffect(() => {
    if (chronoRunning) {
      chronoInterval.current = setInterval(() => {
        setChronoTime((prev) => {
          const t = prev + 1;
          localStorage.setItem('chronoTime', t.toString());
          document.title = `${formatTime(t)} | Yelken Çalışma`;
          return t;
        });
      }, 1000);
    } else {
      if (chronoInterval.current) clearInterval(chronoInterval.current);
      document.title = 'Yelken | Hedeflerine Yelken Aç';
    }
    localStorage.setItem('chronoRunning', JSON.stringify(chronoRunning));
    return () => { if (chronoInterval.current) clearInterval(chronoInterval.current); };
  }, [chronoRunning, formatTime]);

  // ── Timer interval ──
  useEffect(() => {
    if (timerRunning) {
      timerInterval.current = setInterval(() => {
        setTimerRemaining((prev) => {
          if (prev <= 1) {
            return 0;
          }
          const t = prev - 1;
          localStorage.setItem('timerRemaining', t.toString());
          document.title = `${formatTime(t)} | Yelken Çalışma`;
          return t;
        });
      }, 1000);
    } else {
      if (timerInterval.current) clearInterval(timerInterval.current);
      document.title = 'Yelken | Hedeflerine Yelken Aç';
    }
    localStorage.setItem('timerRunning', JSON.stringify(timerRunning));
    return () => { if (timerInterval.current) clearInterval(timerInterval.current); };
  }, [timerRunning, formatTime]);

  // Handle timer reaching zero
  useEffect(() => {
    if (timerRunning && timerRemaining <= 0 && timerStartDuration > 0) {
      if (timerInterval.current) clearInterval(timerInterval.current);
      setTimerRunning(false);
      saveStudyEntry(timerStartDuration);
      localStorage.setItem('timerRunning', 'false');
      localStorage.setItem('timerRemaining', '0');
      document.title = 'Yelken | Hedeflerine Yelken Aç';
      toast.success('Süre doldu! Kayıt eklendi.');
    }
  }, [timerRemaining, timerRunning, timerStartDuration]);

  // ── Save entry ──
  const saveStudyEntry = useCallback(async (durationInSeconds) => {
    if (!durationInSeconds || durationInSeconds <= 0) return;
    const now = new Date();
    const entry = { duration: durationInSeconds, timestamp: Timestamp.fromDate(now), date: now.toISOString().split('T')[0], userId };
    try {
      const ref = await addDoc(collection(db, 'studyEntries'), entry);
      setEntries((prev) => [...prev, { id: ref.id, ...entry }]);
      await updateStreak(userId, 'study');
      toast.success('Çalışma kaydedildi! 🎉');
    } catch (err) { toast.error(`Kayıt hatası: ${err.message}`); }
  }, [userId]);

  // ── Chrono handlers ──
  const handleChronoToggle = () => {
    if (chronoRunning && chronoInterval.current) clearInterval(chronoInterval.current);
    setChronoRunning((p) => !p);
  };
  const handleChronoSave = () => {
    if (chronoInterval.current) clearInterval(chronoInterval.current);
    if (chronoTime > 0) saveStudyEntry(chronoTime);
    setChronoTime(0); setChronoRunning(false);
    localStorage.setItem('chronoTime', '0');
    localStorage.setItem('chronoRunning', 'false');
    document.title = 'Yelken | Hedeflerine Yelken Aç';
  };
  const handleChronoReset = () => {
    if (chronoInterval.current) clearInterval(chronoInterval.current);
    setChronoTime(0); setChronoRunning(false);
    localStorage.setItem('chronoTime', '0');
    localStorage.setItem('chronoRunning', 'false');
    document.title = 'Yelken | Hedeflerine Yelken Aç';
  };

  // ── Timer handlers ──
  const handleTimerStart = () => {
    if (timerRemaining > 0 && !timerRunning) { setTimerRunning(true); return; }
    const total = timerHours * 3600 + timerMinutes * 60 + timerSeconds;
    if (total <= 0) { toast.error('Süre sıfırdan büyük olmalı!'); return; }
    setTimerStartDuration(total); setTimerRemaining(total); setTimerRunning(true);
    localStorage.setItem('timerStartDuration', total.toString());
    localStorage.setItem('timerRemaining', total.toString());
    localStorage.setItem('timerRunning', 'true');
  };
  const handleTimerPause = () => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    setTimerRunning(false);
    localStorage.setItem('timerRunning', 'false');
    document.title = 'Yelken | Hedeflerine Yelken Aç';
  };
  const handleTimerSave = () => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    const elapsed = timerStartDuration - timerRemaining;
    if (elapsed > 0) saveStudyEntry(elapsed);
    handleTimerReset();
  };
  const handleTimerReset = () => {
    if (timerInterval.current) clearInterval(timerInterval.current);
    setTimerRemaining(0); setTimerStartDuration(0); setTimerRunning(false);
    setTimerHours(0); setTimerMinutes(0); setTimerSeconds(0);
    localStorage.setItem('timerRemaining', '0');
    localStorage.setItem('timerStartDuration', '0');
    localStorage.setItem('timerRunning', 'false');
    document.title = 'Yelken | Hedeflerine Yelken Aç';
  };

  // ── Manual add ──
  const handleManualAdd = () => {
    const d = manualHours * 3600 + manualMinutes * 60;
    if (d <= 0) { toast.error('Süre sıfırdan büyük olmalı!'); return; }
    saveStudyEntry(d);
    setManualHours(0); setManualMinutes(0);
  };

  // ── Entry CRUD ──
  const handleDelete = async (id) => {
    try { await deleteDoc(doc(db, 'studyEntries', id)); setEntries((p) => p.filter((e) => e.id !== id)); toast.success('Kayıt silindi.'); }
    catch (err) { toast.error(`Silme hatası: ${err.message}`); }
  };
  const handleEdit = (entry) => {
    setEditingEntryId(entry.id);
    setEditHours(Math.floor(entry.duration / 3600));
    setEditMinutes(Math.floor((entry.duration % 3600) / 60));
    setEditSeconds(entry.duration % 60);
  };
  const handleEditSave = async () => {
    const d = editHours * 3600 + editMinutes * 60 + editSeconds;
    if (d <= 0) { toast.error('Süre sıfırdan büyük olmalı!'); return; }
    try {
      await updateDoc(doc(db, 'studyEntries', editingEntryId), { duration: d });
      setEntries((p) => p.map((e) => e.id === editingEntryId ? { ...e, duration: d } : e));
      setEditingEntryId(null); toast.success('Kayıt güncellendi.');
    } catch (err) { toast.error(`Güncelleme hatası: ${err.message}`); }
  };

  // ── Focus Mode ──
  const displayTime = mode === 'chronometer' ? chronoTime : timerRemaining;
  if (focusMode) {
    return <FocusOverlay displayTime={displayTime} formatTime={formatTime} onExit={() => setFocusMode(false)} />;
  }

  return (
    <div className="study-page">
      <motion.h1 className="study-page-title" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        Çalışma <span className="text-gradient">Takibi</span>
      </motion.h1>

      {/* Mode Toggle */}
      <div className="mode-toggle">
        <button 
          className={`mode-toggle-btn ${mode === 'chronometer' ? 'mode-toggle-btn--active' : ''}`} 
          onClick={() => setMode('chronometer')}
        >
          <FiClock size={14} />
          <span>Kronometre</span>
        </button>
        <button 
          className={`mode-toggle-btn ${mode === 'timer' ? 'mode-toggle-btn--active' : ''}`} 
          onClick={() => setMode('timer')}
        >
          <FiSliders size={14} />
          <span>Sayaç</span>
        </button>
      </div>

      {/* Timer */}
      <TimerDisplay
        mode={mode} chronoTime={chronoTime} chronoRunning={chronoRunning}
        timerRemaining={timerRemaining} timerRunning={timerRunning}
        timerStartDuration={timerStartDuration} formatTime={formatTime}
        onChronoToggle={handleChronoToggle} onChronoSave={handleChronoSave} onChronoReset={handleChronoReset}
        onTimerStart={handleTimerStart} onTimerPause={handleTimerPause} onTimerSave={handleTimerSave} onTimerReset={handleTimerReset}
        timerHours={timerHours} timerMinutes={timerMinutes} timerSeconds={timerSeconds}
        setTimerHours={setTimerHours} setTimerMinutes={setTimerMinutes} setTimerSeconds={setTimerSeconds}
        onFocusMode={() => setFocusMode(true)}
      />
      {/* Minimal action bar */}
      <motion.div 
        className="study-action-bar"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
      >
        <button 
          className="study-action-trigger" 
          onClick={() => setShowManualModal(true)}
        >
          <FiPlus className="study-action-icon" size={16} />
          <span>Manuel Kayıt Ekle</span>
        </button>
        <button 
          className="study-action-trigger" 
          onClick={() => setShowEntriesModal(true)}
        >
          <FiList className="study-action-icon" size={16} />
          <span>Kayıtları Listele</span>
          {entries.length > 0 && (
            <span className="study-action-badge">{entries.length}</span>
          )}
        </button>
      </motion.div>

      {/* Manual Add Modal */}
      <AnimatePresence>
        {showManualModal && (
          <motion.div
            className="study-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowManualModal(false)}
          >
            <motion.div
              className="study-modal"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="study-modal-header">
                <h3>Manuel Kayıt Ekle</h3>
                <button className="study-modal-close" onClick={() => setShowManualModal(false)}>✕</button>
              </div>
              <div className="study-modal-body">
                <div className="manual-add">
                  <div className="manual-add-group">
                    <input type="number" className="input timer-input" min={0} value={manualHours} onChange={(e) => setManualHours(Number(e.target.value))} />
                    <span className="timer-input-label">saat</span>
                  </div>
                  <div className="manual-add-group">
                    <input type="number" className="input timer-input" min={0} max={59} value={manualMinutes} onChange={(e) => setManualMinutes(Number(e.target.value))} />
                    <span className="timer-input-label">dakika</span>
                  </div>
                </div>
                <button 
                  className="study-modal-submit"
                  onClick={() => { handleManualAdd(); setShowManualModal(false); }}
                >
                  Kaydet
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Entries Modal */}
      <AnimatePresence>
        {showEntriesModal && (
          <motion.div
            className="study-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowEntriesModal(false)}
          >
            <motion.div
              className="study-modal study-modal--lg"
              initial={{ opacity: 0, y: 40, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 40, scale: 0.95 }}
              transition={{ duration: 0.25, ease: [0.23, 1, 0.32, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="study-modal-header">
                <h3>Çalışma Kayıtları</h3>
                <button className="study-modal-close" onClick={() => setShowEntriesModal(false)}>✕</button>
              </div>
              <div className="study-modal-body study-modal-body--scroll">
                <StudyEntryList
                  entries={entries} formatTime={formatTime}
                  onDelete={handleDelete} onEdit={handleEdit} onEditSave={handleEditSave} onEditCancel={() => setEditingEntryId(null)}
                  editingEntryId={editingEntryId} editHours={editHours} editMinutes={editMinutes} editSeconds={editSeconds}
                  setEditHours={setEditHours} setEditMinutes={setEditMinutes} setEditSeconds={setEditSeconds}
                  selectedDate={selectedDate} setSelectedDate={setSelectedDate}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      
    </div>
  );
}

export default StudyTimePage;